import { type ReactNode, createContext, useMemo } from 'react'

import { type EnabledItems, useEnabledItems } from '../lib/enabled-items-hook'
import {
  type StepPreviewAnimation,
  defaultStepPreviewAnimation,
  useStepPreviewAnimation,
} from '../lib/preview-animation-hook'
import { type FrameBuilderState, emptyFrameBuilderState } from '../lib/state'
import { useLastValidFrameBuilderState } from '../lib/validation/last-valid-state-hook'
import {
  type ValidationErrors,
  useValidationMessages,
} from '../lib/validation/validation-messages-hook'

interface ValidationContextProps {
  validationMessages: ValidationErrors
  lastValidState: FrameBuilderState
  enabledItems: EnabledItems
  frameCompletionPercentage: number
  stepPreviewAnimation: StepPreviewAnimation
}

interface ValidationProviderProps {
  children: ReactNode
}

export const FrameBuilderValidationContext =
  createContext<ValidationContextProps>({
    validationMessages: {},
    lastValidState: emptyFrameBuilderState,
    enabledItems: {
      profileIds: [],
      passepartoutSizeIds: [],
      passepartoutColorIds: [],
    },
    frameCompletionPercentage: 0,
    stepPreviewAnimation: defaultStepPreviewAnimation,
  })

export const FrameBuilderValidationProvider = ({
  children,
}: ValidationProviderProps) => {
  const lastValidState = useLastValidFrameBuilderState()
  const validationMessages = useValidationMessages()
  const stepPreviewAnimation = useStepPreviewAnimation(validationMessages)

  const enabledItems = useEnabledItems()

  // TODO: Wrong way to check for valid state!
  const frameCompletionPercentage = useMemo(() => {
    const validSelections = [
      !validationMessages?.artowkSizePreview,
      !validationMessages?.frameStyleProfilePreview,
      !validationMessages?.frameStyleTexturePreview,
      !validationMessages?.passepartoutSizePreview,
      !validationMessages?.passepartoutColorPreview,
      !validationMessages?.glassTypePreview,
    ]

    return Math.round(
      (validSelections.filter(Boolean).length / validSelections.length) * 100
    )
  }, [validationMessages])

  return (
    <FrameBuilderValidationContext.Provider
      value={{
        validationMessages,
        lastValidState,
        enabledItems,
        frameCompletionPercentage,
        stepPreviewAnimation,
      }}
    >
      {children}
    </FrameBuilderValidationContext.Provider>
  )
}
